// alldivesiteweather.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Typography, Paper, Tabs, Tab, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../contexts/AuthContext";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import AirIcon from "@mui/icons-material/Air";

// Import components
import CurrentWeather from "./CurrentWeather";
import WeeklyWeather from "./WeeklyWeather";
import WindAndRainData from "./WindAndRainData";

const AllDivesiteWeather = ({ divesiteId }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState("current");
  const [lastUpdated, setLastUpdated] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const { currentUser } = useAuth();
  const theme = useTheme();

  useEffect(() => {
    const fetchWeather = async () => {
      setLoading(true);
      setError(null);
      const token = await currentUser.getIdToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      try {
        const currentWeatherResponse = await axios.get(
          `/api/weather/current/${divesiteId}`,
          { headers }
        );

        const weeklyWeatherResponse = await axios.get(
          `/api/weather/weekly/${divesiteId}`,
          { headers }
        );

        const windWeatherResponse = await axios.get(
          `/api/weather/wind/${divesiteId}`,
          { headers }
        );

        const rainWeatherResponse = await axios.get(
          `/api/weather/rain/${divesiteId}`,
          { headers }
        );

        setWeatherData({
          current: currentWeatherResponse.data,
          weekly: weeklyWeatherResponse.data,
          wind: windWeatherResponse.data,
          rain: rainWeatherResponse.data,
        });

        setLastUpdated({
          current: new Date().toLocaleString(),
          weekly: new Date().toLocaleString(),
          wind: new Date().toLocaleString(),
          rain: new Date().toLocaleString(),
        });
      } catch (err) {
        console.error("Error fetching weather data:", err);
        setError(err.message || "Failed to fetch weather data.");
      } finally {
        setLoading(false);
      }
    };

    if (divesiteId) {
      fetchWeather();
    }
  }, [divesiteId, currentUser]);

  if (loading) {
    return <div>Loading weather data...</div>;
  }

  if (error) {
    return (
      <div>
        Error here: {divesiteId}: {error}
      </div>
    );
  }

  if (!weatherData) {
    return <div>No weather data available.</div>;
  }

  const renderComponent = () => {
    switch (selectedComponent) {
      case "current":
        return (
          <CurrentWeather
            weatherData={weatherData.current}
            lastUpdated={lastUpdated.current}
          />
        );
      case "weekly":
        return (
          <WeeklyWeather
            weatherData={weatherData.weekly}
            lastUpdated={lastUpdated.weekly}
          />
        );
      case "windandrain":
        return (
          <WindAndRainData
            windData={weatherData.wind}
            rainData={weatherData.rain}
            lastUpdated={lastUpdated.wind}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        );
      default:
        return (
          <Box sx={{ p: 2, bgcolor: "background.paper" }}>
            Select a weather component.
          </Box>
        );
    }
  };

  return (
    <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
      <Typography
        variant="h6"
        sx={{ p: 2, fontWeight: "medium" }}
        color="primary"
      >
        Divesite Weather
      </Typography>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          mb: 2,
          mx: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Tabs
          value={selectedComponent}
          onChange={(e, newValue) => setSelectedComponent(newValue)}
          aria-label="weather tabs"
          variant="fullWidth"
          sx={{
            px: 1,
            "& .MuiTabs-indicator": {
              height: 3,
            },
          }}
        >
          <Tab
            icon={<WbSunnyIcon />}
            label="Current"
            value="current"
            sx={{ fontWeight: "medium", py: 1.5 }}
          />
          <Tab
            icon={<AirIcon />}
            label="Wind & Rain"
            value="windandrain"
            sx={{ fontWeight: "medium", py: 1.5 }}
          />
          <Tab
            icon={<CalendarViewWeekIcon />}
            label="Weekly"
            value="weekly"
            sx={{ fontWeight: "medium", py: 1.5 }}
          />
        </Tabs>
      </Paper>
      <Box sx={{ p: 2 }}>{renderComponent()}</Box>
    </Box>
  );
};

export default AllDivesiteWeather;
